<template>
  <sfs-form-modal
    :id="modalId"
    :title="modalTitle"
  >
    <b-form
      novalidate
      @submit.stop.prevent="handleSubmit"
    >
      <b-row>
        <b-col>
          <b-alert
            :show="hasBaseServerErrors()"
            variant="danger"
            dismissible
          >
            <p
              class="mb-0"
              v-for="(error, index) in baseServerErrors"
              :key="index"
            >
              {{ error }}
            </p>
          </b-alert>
        </b-col>
      </b-row>

      <b-row
        v-if="isBulkSelection"
        class="mb-3"
      >
        <b-col cols="12">
          <b-alert
            show
            variant="info"
            class="mb-0"
          >
            Work order completions will be processed in the background. You can continue working while they complete.
          </b-alert>
        </b-col>
      </b-row>

      <b-row
        v-if="isBulkSelection"
        class="mb-3 text-center"
      >
        <b-col cols="12">
          The selected Work Orders will be completed using the values below.
        </b-col>
      </b-row>

      <b-row
        v-if="isBulkSelection && nonCompletableWorkOrders.length"
        class="mb-2"
      >
        <b-col>
          <b-alert
            show
            variant="warning"
          >
            The selected Work Orders can't be completed and will be skipped:
            {{ nonCompletableWorkOrders.map((wo) => wo.number || `#${wo.id}`).join(', ') }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.assigned_to_id"
            label-for="work-order-complete-assigned_to_id"
          >
            <user-select
              id="work-order-complete-assigned_to_id"
              :select-class="{ 'is-invalid': $v.form.assigned_to_id.$dirty && $v.form.assigned_to_id.$error }"
              select-label="name"
              :value="form.assigned_to"
              :filter-params="technicianSelectParams"
              @input="onAssignedToChange"
            />
            <b-form-invalid-feedback
              v-if="$v.form.assigned_to_id.$dirty"
              id="work-order-complete-assigned_to_id-feedback"
            >
              <span v-if="!$v.form.assigned_to_id.required">Please enter an assigned technician.</span>
              <span v-if="!$v.form.assigned_to_id.serverFailed">{{ serverErrors.assigned_to }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.done_by_id"
            label-for="work-order-complete-done_by_id"
          >
            <user-select
              id="work-order-complete-done_by_id"
              :select-class="{ 'is-invalid': $v.form.done_by_id.$dirty && $v.form.done_by_id.$error }"
              select-label="name"
              :disabled="!form.assigned_to_id"
              :technician-manager-id="form.assigned_to_id"
              :primary-option="form.assigned_to"
              :value="form.done_by"
              @input="onDoneByChange"
            />
            <b-form-invalid-feedback
              v-if="$v.form.done_by_id.$dirty"
              id="work-order-complete-done_by_id-feedback"
            >
              <span v-if="!$v.form.done_by_id.required">Please enter the technician the work is completed by.</span>
              <span v-if="!$v.form.done_by_id.serverFailed">{{ serverErrors.done_by }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.checked_in_at"
            label-for="work-order-complete-checked_in_at"
          >
            <sfs-date-time-picker
              id="work-order-complete-checked_in_at"
              v-model="form.checked_in_at"
              :class="{ 'is-invalid': $v.form.checked_in_at.$dirty && $v.form.checked_in_at.$error }"
              :with-class="{ 'is-invalid': $v.form.checked_in_at.$dirty && $v.form.checked_in_at.$error }"
              :time-zone="pickerTimeZone"
            />
            <b-form-invalid-feedback
              v-if="$v.form.checked_in_at.$dirty"
              id="work-order-complete-checked_in_at-feedback"
            >
              <span
                v-if="!$v.form.checked_in_at.required"
                class="mb-0"
              >Please enter a check-in time.</span>
              <span v-if="!$v.form.checked_in_at.serverFailed">{{ serverErrors.checked_in_at }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12">
          <b-form-group
            :label="fieldLabels.checked_out_at"
            label-for="work-order-complete-checked_out_at"
          >
            <sfs-date-time-picker
              id="work-order-complete-checked_out_at"
              v-model="form.checked_out_at"
              :class="{ 'is-invalid': $v.form.checked_out_at.$dirty && $v.form.checked_out_at.$error }"
              :with-class="{ 'is-invalid': $v.form.checked_out_at.$dirty && $v.form.checked_out_at.$error }"
              :time-zone="pickerTimeZone"
            />
            <b-form-invalid-feedback
              v-if="$v.form.checked_out_at.$dirty"
              id="work-order-complete-checked_out_at-feedback"
            >
              <span
                v-if="!$v.form.checked_out_at.required"
                class="mb-0"
              >Please enter a check-out time.</span>
              <span v-if="!$v.form.checked_out_at.serverFailed">{{ serverErrors.checked_out_at }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          v-if="showFollowUpActions"
        >
          <b-form-group
            label="Follow-Up Actions"
            label-for="work-order-complete-follow-up-actions"
          >
            <v-select
              id="work-order-complete-follow-up-actions"
              :options="followUpActionOptions"
              :value="form.follow_up_actions"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              label="label"
              :reduce="option => option.value"
              :filterable="false"
              placeholder="Select follow-up actions"
              @input="form.follow_up_actions = $event"
            >
              <template #option="{ label }">
                {{ label }}
              </template>
              <template #selected-option="{ label }">
                {{ label }}
              </template>
              <template #no-options="{ search, searching }">
                <span v-if="searching">No results found for <em>{{ search }}</em></span>
                <span v-else><em>Focus to show options</em></span>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          v-if="form.follow_up_actions.includes('return_needed')"
        >
          <b-form-group
            label="Return Date and Time"
            label-for="follow-up-date"
          >
            <sfs-date-time-picker
              id="follow-up-date"
              v-model="form.follow_up_at"
              :time-zone="pickerTimeZone"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          v-if="form.follow_up_actions.length > 0"
        >
          <b-form-group
            label="Follow-Up Comments"
            label-for="follow-up-comments"
          >
            <b-form-textarea
              id="follow-up-comments"
              v-model="form.follow_up_notes"
              rows="3"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col
          offset-lg="6"
          lg="3"
          order="2"
          order-lg="1"
        >
          <b-button
            variant="link-dark"
            block
            id="work-order-complete-close-button"
            @click="closeModal"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col
          lg="3"
          order="1"
          order-lg="2"
        >
          <b-button
            id="work-order-complete-submit-button"
            block
            type="submit"
            variant="primary"
            :disabled="processing"
          >
            Save
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </sfs-form-modal>
</template>

<script>
import { DateTime } from 'luxon';
import { required } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import formModalMixin from '@/mixins/formModalMixin';
import { WORK_ORDER_FORM_LABELS } from '@/constants/workOrders';
import { USER_TECHNICIAN_CATEGORY } from '@/constants/users';

import UserSelect from '@/components/admin/users/Select.vue';

export default {
  name: 'WorkOrdersCompleteModal',
  components: {
    UserSelect,
  },
  mixins: [formMixin, formModalMixin],
  props: {
    value: {
      type: Object,
      default: null,
    },
    workOrders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const source = this.value || {};
    return {
      form: {
        assigned_to: source.assigned_to,
        assigned_to_id: source.assigned_to_id,
        done_by: source.done_by || source.assigned_to,
        done_by_id: source.done_by_id || source.assigned_to_id,
        checked_in_at: source.checked_in_at,
        checked_out_at: source.checked_out_at,
        follow_up_actions: source.follow_up_actions || [],
        follow_up_notes: source.follow_up_notes || '',
        follow_up_at: source.follow_up_at || null,
      },
      followUpActionOptions: [
        { value: 'return_needed', label: 'Return is needed' },
        { value: 'quote_needed', label: 'Quote is needed' },
        { value: 'parts_needed', label: 'Parts are needed' },
      ],
      fieldLabels: { ...WORK_ORDER_FORM_LABELS },
      technicianSelectParams: { category: USER_TECHNICIAN_CATEGORY, active: true },
      modalId: 'work-order-complete-modal',
    };
  },
  computed: {
    isBulkSelection() {
      return Array.isArray(this.workOrders) && this.workOrders.length > 0;
    },
    modalTitle() {
      if (this.isBulkSelection) {
        return `Complete ${this.completableWorkOrders.length} Work Orders`;
      }
      return 'Complete Work Order';
    },
    completableWorkOrders() {
      if (!this.isBulkSelection) {
        return [];
      }
      return this.workOrders.filter((workOrder) => this.$can('complete', workOrder));
    },
    nonCompletableWorkOrders() {
      if (!this.isBulkSelection) {
        return [];
      }
      return this.workOrders.filter((workOrder) => !this.$can('complete', workOrder));
    },
    showFollowUpActions() {
      if (this.isBulkSelection) {
        return this.completableWorkOrders.length > 0
          && this.completableWorkOrders.every((workOrder) => workOrder.client?.work_order_follow_up_actions);
      }
      return Boolean(this.value?.client?.work_order_follow_up_actions);
    },
    pickerTimeZone() {
      if (!this.isBulkSelection) {
        return this.value?.location?.time_zone;
      }
      const timeZones = [...new Set(
        this.completableWorkOrders.map((workOrder) => workOrder.location?.time_zone).filter(Boolean),
      )];
      return timeZones.length === 1 ? timeZones[0] : undefined;
    },
  },
  mounted() {
    this.$root.$on('bv::modal::show', this.onModalShow);
  },
  beforeDestroy() {
    this.$root.$off('bv::modal::show', this.onModalShow);
  },
  validations: {
    form: {
      assigned_to_id: {
        required,
        serverFailed() {
          return !this.hasServerErrors('assigned_to');
        },
      },
      done_by_id: {
        required,
        serverFailed() {
          return !this.hasServerErrors('done_by');
        },
      },
      checked_in_at: {
        required,
        serverFailed() {
          return !this.hasServerErrors('checked_in_at');
        },
      },
      checked_out_at: {
        required,
        serverFailed() {
          return !this.hasServerErrors('checked_out_at');
        },
      },
    },
  },
  methods: {
    onModalShow(event, modalId) {
      if (modalId !== this.modalId) {
        return;
      }

      if (this.isBulkSelection) {
        const checkedOutAt = DateTime.now().setZone(this.pickerTimeZone || undefined).toISO();
        this.form.checked_out_at = checkedOutAt;
        this.form.checked_in_at = checkedOutAt;
        return;
      }

      const source = this.value || {};
      const timeZone = source.location && source.location.time_zone;
      const checkedOutAt = source.checked_out_at || DateTime.now().setZone(timeZone || undefined).toISO();
      this.form.checked_out_at = checkedOutAt;

      const estimatedHoursRaw = source.service?.estimated_hours ?? source.estimated_hours;
      const estimatedHours = Number(estimatedHoursRaw);
      this.form.checked_in_at = source.checked_in_at
        || (
          Number.isFinite(estimatedHours) && estimatedHours > 0
            ? DateTime.fromISO(checkedOutAt).minus({ hours: estimatedHours }).toISO()
            : checkedOutAt
        );
    },
    onAssignedToChange(user) {
      this.form.assigned_to_id = user ? user.id : '';
      this.form.assigned_to = user;
      this.form.done_by_id = user ? user.id : '';
      this.form.done_by = user;
    },
    onDoneByChange(user) {
      this.form.done_by_id = user ? user.id : '';
      this.form.done_by = user;
    },
    handleSubmit() {
      if (this.hasFormErrors()) {
        return;
      }

      if (this.isBulkSelection) {
        if (!this.completableWorkOrders.length) {
          return;
        }
        this.processing = true;
        this.$workOrdersAPI
          .bulkCompleteWorkOrders({}, this.buildBulkCompletePayload())
          .then((response) => {
            this.resetServerErrors();
            this.$v.$reset();
            this.showBackgroundProcessingToast();
            this.$emit('completed', response);
            this.closeModal();
            this.processing = false;
          })
          .catch((error) => {
            this.processServerErrors(error.response.data.errors);
            this.processing = false;
          });
        return;
      }

      this.processing = true;
      this.$workOrdersAPI
        .complete(this.value.id, this.form)
        .then((workOrder) => {
          this.resetServerErrors();
          this.$v.$reset();
          this.$emit('input', workOrder);
          this.closeModal();
          this.processing = false;
        })
        .catch((error) => {
          this.processServerErrors(error.response.data.errors);
          this.processing = false;
        });
    },
    buildBulkCompletePayload() {
      const payload = {
        work_order_ids: this.completableWorkOrders.map((workOrder) => Number(workOrder.id)),
        assigned_to_id: this.form.assigned_to_id,
        done_by_id: this.form.done_by_id,
        checked_in_at: this.form.checked_in_at,
        checked_out_at: this.form.checked_out_at,
      };
      if (this.showFollowUpActions && this.form.follow_up_actions.length) {
        payload.follow_up_actions = this.form.follow_up_actions;
        if (this.form.follow_up_notes) {
          payload.follow_up_notes = this.form.follow_up_notes;
        }
        if (this.form.follow_up_actions.includes('return_needed') && this.form.follow_up_at) {
          payload.follow_up_at = this.form.follow_up_at;
        }
      }
      return payload;
    },
    showBackgroundProcessingToast() {
      if (!this.$bvToast) {
        return;
      }

      this.$bvToast.toast('Work order completions are being processed in the background.', {
        title: 'Completions queued',
        toaster: 'b-toaster-bottom-right',
        variant: 'info',
        autoHideDelay: 5000,
        appendToast: false,
      });
    },
  },
};
</script>
